import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDown, LogOut, User as UserIcon, Shield, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const LOGO = "https://img1.wsimg.com/isteam/ip/f6017c9d-e427-4e2e-9a8f-98cea9502516/RGULOGO_NAVY_MatchStamp.jpeg/:/rs=h:174,cg:true,m/qt=q:100/ll";

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-slate-200" data-testid="rgu-header">
      <div className="rgu-container flex items-center justify-between h-20 gap-4">
        <Link to="/" className="flex items-center gap-3 min-w-0 flex-shrink-0" data-testid="header-logo-link">
          <img src={LOGO} alt="Real Game Used" className="h-10 lg:h-12 w-auto" />
          <div className="hidden sm:block leading-tight">
            <div className="font-display text-sm xl:text-base text-[#0A192F] whitespace-nowrap">REAL GAME USED</div>
            <div className="rgu-eyebrow text-slate-500 text-[9px] xl:text-[10px] whitespace-nowrap">Authentication · Est. 2016</div>
          </div>
        </Link>

        {/* Desktop nav (only at xl+) */}
        <nav className="hidden xl:flex items-center gap-6 rgu-eyebrow text-slate-600 text-[11px]">
          <Link to="/" data-testid="nav-home" className="hover:text-[#0A192F]">Home</Link>
          <a href="/#services" data-testid="nav-services" className="hover:text-[#0A192F]">Services</a>
          <a href="/#pricing" data-testid="nav-pricing" className="hover:text-[#0A192F]">Pricing</a>
          <a href="https://infiniteauctions.com/consign" target="_blank" rel="noreferrer" data-testid="nav-consign" className="hover:text-[#0A192F] whitespace-nowrap">
            Consign to Auction
          </a>
          {user && (
            <Link to="/submissions" data-testid="nav-submissions" className="hover:text-[#0A192F] whitespace-nowrap">Submissions</Link>
          )}
          {user?.role === "admin" && (
            <Link to="/admin" data-testid="nav-admin" className="text-[#DC2626] hover:text-red-800">Admin</Link>
          )}
        </nav>

        {/* Right-side actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Authenticate Now dropdown — hidden on very small screens, shown from sm+ */}
          <div className="hidden sm:block">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="rgu-btn-primary text-[10px] md:text-xs uppercase tracking-widest px-3 md:px-5 py-2 md:py-3"
                  data-testid="authenticate-now-btn"
                >
                  Authenticate Now <ChevronDown className="w-3 h-3 md:w-4 md:h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-none border border-[#0A192F] w-72 p-0">
                <DropdownMenuItem
                  className="rounded-none px-5 py-4 cursor-pointer focus:bg-slate-50 border-b border-slate-200"
                  onClick={() => navigate(user ? "/submit/photo_match" : "/login?next=/submit/photo_match")}
                  data-testid="dropdown-photo-match"
                >
                  <div>
                    <div className="font-display text-sm text-[#0A192F]">Photo Match Authentication</div>
                    <div className="text-xs text-slate-500 mt-1">Conclusive validation. Up to 1,000%+ value lift.</div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="rounded-none px-5 py-4 cursor-pointer focus:bg-slate-50"
                  onClick={() => navigate(user ? "/submit/traditional" : "/login?next=/submit/traditional")}
                  data-testid="dropdown-traditional"
                >
                  <div>
                    <div className="font-display text-sm text-[#0A192F]">Traditional Authentication</div>
                    <div className="text-xs text-slate-500 mt-1">In-person mail-in evaluation against exemplars.</div>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="hidden sm:flex items-center gap-2 text-sm border border-slate-300 px-3 py-2.5 hover:bg-slate-50" data-testid="user-menu-trigger">
                  <UserIcon className="w-4 h-4" />
                  <span className="hidden md:inline max-w-[100px] truncate">{user.name || user.email}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-none">
                <DropdownMenuItem onClick={() => navigate("/submissions")} className="rounded-none cursor-pointer" data-testid="menu-submissions">
                  My Submissions
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/account")} className="rounded-none cursor-pointer" data-testid="menu-account">
                  My Account
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} className="rounded-none cursor-pointer" data-testid="menu-admin">
                    <Shield className="w-4 h-4 mr-2" /> Admin Console
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} className="rounded-none cursor-pointer" data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link
              to="/login"
              className="hidden sm:inline-block text-[11px] uppercase tracking-widest text-[#0A192F] hover:underline px-2 font-mono-rgu"
              data-testid="header-login"
            >
              Sign in
            </Link>
          )}

          {/* Mobile hamburger */}
          <button
            className="xl:hidden p-2 border border-slate-300"
            onClick={() => setMenuOpen((p) => !p)}
            data-testid="mobile-menu-toggle"
            aria-label="Open menu"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {menuOpen && (
        <div className="xl:hidden border-t border-slate-200 bg-white" data-testid="mobile-menu">
          <nav className="rgu-container py-4 flex flex-col gap-1 text-sm">
            <Link to="/" onClick={() => setMenuOpen(false)} className="py-2.5 border-b border-slate-100 text-[#0A192F]">Home</Link>
            <a href="/#services" onClick={() => setMenuOpen(false)} className="py-2.5 border-b border-slate-100 text-[#0A192F]">Services</a>
            <a href="/#pricing" onClick={() => setMenuOpen(false)} className="py-2.5 border-b border-slate-100 text-[#0A192F]">Pricing</a>
            <a href="https://infiniteauctions.com/consign" target="_blank" rel="noreferrer" className="py-2.5 border-b border-slate-100 text-[#0A192F]">
              Consign to Auction
            </a>
            <button
              onClick={() => { setMenuOpen(false); navigate(user ? "/submit/photo_match" : "/login?next=/submit/photo_match"); }}
              className="py-2.5 border-b border-slate-100 text-left text-[#0A192F]"
              data-testid="mobile-photo-match"
            >
              Photo Match Authentication
            </button>
            <button
              onClick={() => { setMenuOpen(false); navigate(user ? "/submit/traditional" : "/login?next=/submit/traditional"); }}
              className="py-2.5 border-b border-slate-100 text-left text-[#0A192F]"
              data-testid="mobile-traditional"
            >
              Traditional Authentication
            </button>
            {user && (
              <>
                <Link to="/submissions" onClick={() => setMenuOpen(false)} className="py-2.5 border-b border-slate-100 text-[#0A192F]">My Submissions</Link>
                <Link to="/account" onClick={() => setMenuOpen(false)} className="py-2.5 border-b border-slate-100 text-[#0A192F]">My Account</Link>
              </>
            )}
            {user?.role === "admin" && (
              <Link to="/admin" onClick={() => setMenuOpen(false)} className="py-2.5 border-b border-slate-100 text-[#DC2626]">Admin Console</Link>
            )}
            {user ? (
              <button onClick={() => { logout(); setMenuOpen(false); navigate("/"); }} className="py-2.5 text-left text-[#0A192F]" data-testid="mobile-logout">
                Sign out
              </button>
            ) : (
              <Link to="/login" onClick={() => setMenuOpen(false)} className="py-2.5 text-[#0A192F]">Sign in</Link>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
